import {
  Component,
  Input,
  ChangeDetectorRef,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  FormArray,
  Validators,
} from '@angular/forms';

import { BaseRepository } from '@interfaces/base-repository';

@Component({
  selector: 'app-multi-select-list',
  templateUrl: './multi-select-list.component.html',
  styleUrls: ['./multi-select-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectListComponent),
      multi: true,
    },
  ],
})
export class MultiSelectListComponent<T extends BaseRepository>
  implements ControlValueAccessor
{
  @Input() allItems: T[] = [];
  @Input() label: string = '';
  @Input() errorMsg: string = '';
  @Input() title?: string;
  @Input() required: boolean = true;
  @Input() minItems: number = 1;

  formArray: FormArray;
  selectableItems: T[] = [];
  isDisabled: boolean = false;

  private onChange: (value: T[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
  ) {
    this.formArray = this.fb.array([]);
  }

  writeValue(value: T[]): void {
    this.formArray.clear();
    if (value && value.length) {
      value.forEach((item) => this.addItem(item));
    } else {
      this.addItem();
    }
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  addItem(item?: T): void {
    if (!item) {
      this.generateSelectableItems();
    }
    const control = this.fb.control(item || null);
    if (this.required) {
      control.setValidators(Validators.required);
    }
    this.formArray.push(control);
    this.cd.detectChanges();
    this.emitValue();
  }

  removeItem(index: number): void {
    this.formArray.removeAt(index);
    this.generateSelectableItems();
    this.emitValue();
  }

  onItemChange(): void {
    this.generateSelectableItems();
    this.emitValue();
  }

  private generateSelectableItems(): void {
    const alreadyUsed = this.formArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);

    this.selectableItems = [...this.allItems].filter(
      (item) => !alreadyUsed.map((used) => used.id).includes(item.id),
    );
  }

  private emitValue(): void {
    const values = this.formArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);
    this.onChange(values);
  }

  get canRemove(): boolean {
    return this.formArray.controls.length > this.minItems;
  }
}
